import React, { lazy, Suspense } from "react"

// Lazy load all page components for code splitting
const StaticHomePage = lazy(() => import("./static/home/index"))
const AboutUsPage = lazy(() => import("./static/company/about_us"))
const BrandPage = lazy(() => import("./static/company/brand"))
const PricingPage = lazy(() => import("./static/pricing/index"))

const ApplicationUserOnboarding = lazy(() => import("./users/onboarding/index"))
const UserDashboardIndexPage = lazy(() => import("./user/dashboard"))

const AdventureIndexPage = lazy(() => import("./adventure/index"))
const ArcadeIndexPage = lazy(() => import("./adventure/arcade/index"))
const ArcadeGamePlayPage = lazy(() => import("./adventure/arcade/play"))
const AdventureRewardsPage = lazy(() => import("./adventure/rewards/index"))
const AdventureAvatarsIndexPage = lazy(() =>
  import("./adventure/avatars/index")
)
const AdventureBannersIndexPage = lazy(() =>
  import("./adventure/banners/index")
)
const AdventureBannersShowPage = lazy(() => import("./adventure/banners/show"))
const AdventureArtCommissionIndexPage = lazy(() =>
  import("./adventure/art_commissions/index")
)
const AdventureDemoIndexPage = lazy(() => import("./adventure/demo/index"))
const AdventureWikiLevelsPage = lazy(() =>
  import("./adventure/wiki/levels/index")
)
const AdventureAbilitiesPage = lazy(() => import("./adventure/abilities/index"))
const StoryIndexPage = lazy(() => import("./adventure/stories/index"))
const AdventureStoriesNewPage = lazy(() => import("./adventure/stories/new"))
const AdventureStoriesShowPage = lazy(() => import("./adventure/stories/show"))
const AdventureStoriesEditPage = lazy(() => import("./adventure/stories/edit"))

const AdventureLeaderboardApp = lazy(() =>
  import("./adventure/leaderboard/index")
)
const AdventureInventoryApp = lazy(() => import("./adventure/inventory/index"))

const WikiApp = lazy(() => import("./wiki/app"))

const CommunityIndexPage = lazy(() => import("./community/index"))
const CommunityRulesPage = lazy(() =>
  import("./static/company/community_rules")
)

const ResourceIndexPage = lazy(() => import("./resources/index"))
const CodexIndexPage = lazy(() => import("./codex/index"))
const CodexShowPage = lazy(() => import("./codex/show"))
const CodexNewPage = lazy(() => import("./codex/new"))
const CodexEditPage = lazy(() => import("./codex/edit"))
const CodexManagePage = lazy(() => import("./codex/manage"))
const TvPage = lazy(() => import("./tv"))
const ChatPage = lazy(() => import("./chat"))
const ChatObserverPage = lazy(() => import("./chat_observer"))
const SearchResultsPage = lazy(() => import("./search"))
const MyHomePage = lazy(() => import("./my/home"))
const MyCodexPage = lazy(() => import("./my/codex"))
const MyCommunityResourcesPage = lazy(() => import("./my/community_resources"))
const CheatsheetShowPage = lazy(() => import("./cheatsheet/show"))
const MySavedPage = lazy(() => import("./my/saved"))
const CalendarEventShowPage = lazy(() => import("./calendar/show"))
const CalendarEventIrishWeek = lazy(() => import("./calendar/show/irish-week"))
const CalendarEventIrishWeekGuestbook = lazy(() =>
  import("./calendar/guestbook")
)
const ChangelogIndexPage = lazy(() => import("./changelog/index"))
const ChangelogShowPage = lazy(() => import("./changelog/show"))
const InterviewIndexPage = lazy(() => import("./interviews/index"))
const InterviewShowPage = lazy(() => import("./interviews/show"))
const ResourceCalendarPage = lazy(() => import("./calendar/index"))
const ResourceCollectionsIndexPage = lazy(() => import("./collections/index"))
const ResourceCollectionsShowPage = lazy(() => import("./collections/show"))
const CollectionsNewPage = lazy(() => import("./collections/new"))
const CollectionsEditPage = lazy(() => import("./collections/edit"))
const CollectionCmsEditPage = lazy(() => import("./collections/cms_edit"))
const TopicsIndexPage = lazy(() => import("./topics/index"))
const TopicsShowPage = lazy(() => import("./topics/show"))

const ProfilesShowPage = lazy(() => import("./profiles/show"))
const EmailNotificationsShowPage = lazy(() =>
  import("./email_notifications/show")
)

// Library pages
const LibraryIndexPage = lazy(() =>
  import("./library/index").then((m) => ({ default: m.LibraryIndexPage }))
)
const LibraryNewPage = lazy(() => import("./library/new"))
const LibraryTypeIndexPage = lazy(() =>
  import("./library/index").then((m) => ({ default: m.LibraryTypeIndexPage }))
)
const LibraryShowPage = lazy(() =>
  import("./library/index").then((m) => ({ default: m.LibraryShowPage }))
)
const LibraryEditPage = lazy(() =>
  import("./library/index").then((m) => ({ default: m.LibraryEditPage }))
)

import AppShell from "design_system/application/providers/app_shell"
import Bugsnag from "@bugsnag/js"
import BugsnagPluginReact from "@bugsnag/plugin-react"

import seamusLoading from "images/love_irish/adventure/characters/seamus-idle.gif"
import pixie from "design_system/adventure/codex/characters/pixie"
import SpriteAnimator from "design_system/core/hooks/use_sprite_animation/component"

const clientStage = window?.LoveIrish?.client?.stage || "development"
const bugsnagApiKey = window?.LoveIrish?.apiKeys?.bugsnag

Bugsnag.start({
  apiKey: bugsnagApiKey,
  releaseStage: clientStage,
  redactedKeys: [
    "access_token", // exact match: "access_token"
    /^password$/i, // case-insensitive: "password", "PASSWORD", "PaSsWoRd"
    /^cc_/,
  ],
  enabledReleaseStages: ["production", "staging"],
  plugins: [new BugsnagPluginReact()],
})

// @ts-expect-error We are defining the plugin above
const ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React)

const ErrorView = () => (
  <div className="container text-center min-h-[500px] flex items-center justify-center">
    <div className="max-w-lg">
      <SpriteAnimator
        className="inline-block mb-12"
        sprite={pixie.sprite}
        width={16}
        height={16}
        offset={pixie.offset}
        frameCount={4}
        shouldAnimate
        scale={4}
        fps={5}
        {...(pixie.spriteSettings || {})}
      />
      <h1 className="post-title text-2xl mb-4">Whoops!</h1>
      <p>
        There was a problem on this page and we have been notified of the issue.
        Please try again later or email us at{" "}
        <strong>
          <a href="mailto:support@love.irish">support@love.irish</a>
        </strong>
      </p>
    </div>
  </div>
)

// Loading fallback for lazy loaded pages
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <figure className="p-4">
      <img
        src={seamusLoading}
        alt="Séamus idle animated pixel art"
        className="mx-auto pixel-art w-[64px]"
      />
      <figcaption className="text-center mt-2">Loading...</figcaption>
    </figure>
  </div>
)

// Returns a plain React component (previously a react_on_rails render-function). The Vite mount
// bootstrap renders every registry entry uniformly via React.createElement(Component, props).
const wrapped = (Component, { renderActionMenu = true } = {}) => {
  return function WrappedPage({ currentUser, currentProfile, pageProps, ...rest }) {
    const mergedProps = Object.assign(pageProps ?? {}, rest)

    if (bugsnagApiKey) {
      if (currentUser) {
        Bugsnag.setUser(currentUser.id, currentUser.email)
        Bugsnag.addMetadata("User", {
          profile: currentUser.slug,
        })
      }
      Bugsnag.addMetadata("Template", {
        Component: Component.name,
        pageProps: mergedProps,
      })
    }

    return (
      <ErrorBoundary FallbackComponent={ErrorView}>
        <Suspense fallback={<PageLoadingFallback />}>
          <AppShell
            currentUser={currentUser}
            currentProfile={currentProfile}
            pageProps={mergedProps}
            renderActionMenu={renderActionMenu}
          >
            <Component {...mergedProps} />
          </AppShell>
        </Suspense>
      </ErrorBoundary>
    )
  }
}

export default {
  StaticHomePage: wrapped(StaticHomePage),
  AboutUsPage: wrapped(AboutUsPage),
  BrandPage: wrapped(BrandPage),
  PricingPage: wrapped(PricingPage),

  UserOnboarding: wrapped(ApplicationUserOnboarding),
  UserDashboardIndexPage: wrapped(UserDashboardIndexPage),

  ProfilesShowPage: wrapped(ProfilesShowPage),

  AdventureIndexPage: wrapped(AdventureIndexPage),
  ArcadeIndexPage: wrapped(ArcadeIndexPage),
  ArcadeGamePlayPage: wrapped(ArcadeGamePlayPage),
  AdventureRewardsPage: wrapped(AdventureRewardsPage),
  AdventureAvatarsIndexPage: wrapped(AdventureAvatarsIndexPage),
  AdventureBannersIndexPage: wrapped(AdventureBannersIndexPage),
  AdventureBannersShowPage: wrapped(AdventureBannersShowPage),
  AdventureArtCommissionIndexPage: wrapped(AdventureArtCommissionIndexPage),
  AdventureDemoIndexPage: wrapped(AdventureDemoIndexPage),
  LeaderboardApp: wrapped(AdventureLeaderboardApp),
  InventoryPage: wrapped(AdventureInventoryApp),
  AdventureWikiLevelsPage: wrapped(AdventureWikiLevelsPage),
  AdventureAbilitiesPage: wrapped(AdventureAbilitiesPage),
  StoryIndexPage: wrapped(StoryIndexPage),
  NewStoryPage: wrapped(AdventureStoriesNewPage),
  StoryTellerPage: wrapped(AdventureStoriesShowPage),
  StoryEditorPage: wrapped(AdventureStoriesEditPage),

  WikiApp: wrapped(WikiApp),

  CommunityIndexPage: wrapped(CommunityIndexPage),
  CommunityRulesPage: wrapped(CommunityRulesPage),

  ResourceIndexPage: wrapped(ResourceIndexPage),
  CodexIndexPage: wrapped(CodexIndexPage),
  CodexShowPage: wrapped(CodexShowPage),
  CodexNewPage: wrapped(CodexNewPage),
  // Fullscreen (@fullscreen in the controller → renderContext: "fullscreen") drops the action menu.
  TvPage: wrapped(TvPage),
  ChatPage: wrapped(ChatPage),
  ChatObserverPage: wrapped(ChatObserverPage),
  SearchResultsPage: wrapped(SearchResultsPage),
  MyHomePage: wrapped(MyHomePage),
  MyCodexPage: wrapped(MyCodexPage),
  MyCommunityResourcesPage: wrapped(MyCommunityResourcesPage),
  // Full-screen editor: no floating action menu (it would sit over the Save button).
  CodexEditPage: wrapped(CodexEditPage, { renderActionMenu: false }),
  CodexManagePage: wrapped(CodexManagePage, { renderActionMenu: false }),
  CheatsheetShowPage: wrapped(CheatsheetShowPage),
  MySavedPage: wrapped(MySavedPage),

  CollectionsIndexPage: wrapped(ResourceCollectionsIndexPage),
  CollectionsShowPage: wrapped(ResourceCollectionsShowPage),
  CollectionsNewPage: wrapped(CollectionsNewPage),
  CollectionsEditPage: wrapped(CollectionsEditPage),
  // Full-screen CMS editor: no floating action menu (it would sit over the panel).
  CollectionCmsEditorPage: wrapped(CollectionCmsEditPage, { renderActionMenu: false }),

  TopicsIndexPage: wrapped(TopicsIndexPage),
  TopicsShowPage: wrapped(TopicsShowPage),

  ResourceCalendarPage: wrapped(ResourceCalendarPage),
  CalendarEventShowPage: wrapped(CalendarEventShowPage),
  CalendarEventIrishWeek: wrapped(CalendarEventIrishWeek),
  CalendarEventIrishWeekGuestbook: wrapped(CalendarEventIrishWeekGuestbook),

  InterviewIndexPage: wrapped(InterviewIndexPage),
  InterviewShowPage: wrapped(InterviewShowPage),

  ChangelogIndexPage: wrapped(ChangelogIndexPage),
  ChangelogShowPage: wrapped(ChangelogShowPage),

  EmailNotificationsShowPage: wrapped(EmailNotificationsShowPage),

  // Library pages
  LibraryIndexPage: wrapped(LibraryIndexPage),
  LibraryNewPage: wrapped(LibraryNewPage),
  LibraryTypeIndexPage: wrapped(LibraryTypeIndexPage),
  LibraryShowPage: wrapped(LibraryShowPage),
  LibraryEditPage: wrapped(LibraryEditPage),
}
